// @core/stores/product/ProductStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { productApi } from '@core/api';
import type {
  CreateProductCategoryRequest,
  CreateProductDependencyRequest,
  CreateProductProfileRequest,
  CreateProductRequest,
  CreateProductReviewRequest,
  ListProductCategoriesResponse,
  ListProductDependenciesResponse,
  ListProductProfilesResponse,
  ListProductReviewsResponse,
  MarketplaceMetadataResponse,
  Product,
  ProductCategoryResponse,
  ProductDependencyResponse,
  ProductProfileResponse,
  ProductResponse,
  ProductReviewResponse,
  PublishProductRequest,
  SetProductCategoryRequest,
  SetProductStatusRequest,
  UpdateMarketplaceMetadataRequest,
  UpdateProductCategoryRequest,
  UpdateProductDependencyRequest,
  UpdateProductProfileRequest,
  UpdateProductRequest,
  ValidateProductDependenciesRequest,
  ValidateProductDependenciesResponse,
} from '@core/types/product';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class ProductStore {
  // List state
  items: Product[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Product | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Product>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Product | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Product | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Register a new product
   */
  async CreateProduct(data: CreateProductRequest): Promise<Product> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await productApi.CreateProduct(data);
      const item = response.product;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Create product category
   */
  async CreateProductCategory(data: CreateProductCategoryRequest): Promise<ProductCategoryResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await productApi.CreateProductCategory(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create product dependency
   */
  async CreateProductDependency(product_id: string, data: CreateProductDependencyRequest): Promise<ProductDependencyResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await productApi.CreateProductDependency(product_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create profile in product (first access)
   */
  async CreateProductProfile(product_id: string, data: CreateProductProfileRequest): Promise<ProductProfileResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await productApi.CreateProductProfile(product_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create product review
   */
  async CreateProductReview(product_id: string, data: CreateProductReviewRequest): Promise<ProductReviewResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await productApi.CreateProductReview(product_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Delete product
   */
  async DeleteProduct(product_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await productApi.DeleteProduct(product_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== product_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === product_id) {
          this.currentItem = null;
        }

        this.removeFromCache(product_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Delete product category
   */
  async DeleteProductCategory(category_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await productApi.DeleteProductCategory(category_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== category_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === category_id) {
          this.currentItem = null;
        }

        this.removeFromCache(category_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Delete product dependency
   */
  async DeleteProductDependency(dependency_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await productApi.DeleteProductDependency(dependency_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== dependency_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === dependency_id) {
          this.currentItem = null;
        }

        this.removeFromCache(dependency_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Get marketplace metadata
   */
  async GetMarketplaceMetadata(product_id: string): Promise<MarketplaceMetadataResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await productApi.GetMarketplaceMetadata(product_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get my profile in this product
   */
  async GetMyProductProfile(product_id: string): Promise<ProductProfileResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await productApi.GetMyProductProfile(product_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get product details
   */
  async GetProduct(product_id: string): Promise<Product> {
    // Check cache first
    const cached = this.cache.get(product_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await productApi.GetProduct(product_id);
      const item = response.product;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Get product by slug
   */
  async GetProductBySlug(slug: string): Promise<Product> {
    // Check cache first
    const cached = this.cache.get(slug);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await productApi.GetProductBySlug(slug);
      const item = response.product;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Get product dependency details
   */
  async GetProductDependency(dependency_id: string): Promise<ProductDependencyResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await productApi.GetProductDependency(dependency_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List products that depend on this product
   */
  async ListDependentProducts(product_id: string, params?: PaginatedRequest): Promise<ListProductDependenciesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await productApi.ListDependentProducts(product_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List my profiles across all products
   */
  async ListMyProductProfiles(params?: PaginatedRequest): Promise<ListProductProfilesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await productApi.ListMyProductProfiles(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List product categories
   */
  async ListProductCategories(params?: PaginatedRequest): Promise<ListProductCategoriesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await productApi.ListProductCategories(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List product dependencies
   */
  async ListProductDependencies(product_id: string, params?: PaginatedRequest): Promise<ListProductDependenciesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await productApi.ListProductDependencies(product_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List product reviews
   */
  async ListProductReviews(product_id: string, params?: PaginatedRequest & { sort_by?: string | number | boolean; status?: string | number | boolean; }): Promise<ListProductReviewsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await productApi.ListProductReviews(product_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List available products
   */
  async ListProducts(params?: PaginatedRequest & { category?: string | number | boolean; is_builtin?: string | number | boolean; search?: string | number | boolean; status?: string | number | boolean; }): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await productApi.ListProducts(params);

      runInAction(() => {
        this.items = response.products?.items ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List products by category
   */
  async ListProductsByCategory(category_id: string, params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await productApi.ListProductsByCategory(category_id, params);

      runInAction(() => {
        this.items = response.products?.items ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Publish product to catalog
   */
  async PublishProduct(product_id: string, data: PublishProductRequest): Promise<ProductResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await productApi.PublishProduct(product_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Remove product from category
   */
  async RemoveProductCategory(product_id: string, category_id: string): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await productApi.RemoveProductCategory(product_id, category_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Assign product to category
   */
  async SetProductCategory(product_id: string, data: SetProductCategoryRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await productApi.SetProductCategory(product_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Set product status
   */
  async SetProductStatus(product_id: string, data: SetProductStatusRequest): Promise<ProductResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await productApi.SetProductStatus(product_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update marketplace metadata
   */
  async UpdateMarketplaceMetadata(product_id: string, data: UpdateMarketplaceMetadataRequest): Promise<MarketplaceMetadataResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await productApi.UpdateMarketplaceMetadata(product_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update product
   */
  async UpdateProduct(product_id: string, data: UpdateProductRequest): Promise<Product> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await productApi.UpdateProduct(product_id, data);
      const item = response.product;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Update product category
   */
  async UpdateProductCategory(category_id: string, data: UpdateProductCategoryRequest): Promise<ProductCategoryResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await productApi.UpdateProductCategory(category_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update product dependency
   */
  async UpdateProductDependency(dependency_id: string, data: UpdateProductDependencyRequest): Promise<ProductDependencyResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await productApi.UpdateProductDependency(dependency_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update my profile in this product
   */
  async UpdateProductProfile(product_id: string, data: UpdateProductProfileRequest): Promise<ProductProfileResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await productApi.UpdateProductProfile(product_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Validate product dependencies
   */
  async ValidateProductDependencies(product_id: string, data: ValidateProductDependenciesRequest): Promise<ValidateProductDependenciesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await productApi.ValidateProductDependencies(product_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const productStore = new ProductStore();
